import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import CryptoJS from 'crypto-js';
import { validateEmail } from './utilities';

/**
 * Normalizes an email address for consistent hashing
 * - Converts to lowercase
 * - Trims whitespace
 * @param {string} email - Email address to normalize
 * @returns {string} - Normalized email or empty string
 */
export const normalizeEmail = (email) => {
    if (!email || typeof email !== 'string') return '';
    return email.toLowerCase().trim();
};

/**
 * Normalizes a phone number to E.164 format
 * @param {string} phone - Phone number to normalize
 * @param {string} defaultCountry - Default country code (e.g., 'US', 'DZ')
 * @returns {string} - E.164 formatted phone number or empty string
 */
export const normalizePhone = (phone, defaultCountry = 'US') => {
    if (!phone || typeof phone !== 'string') return '';
    
    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, '');
    
    if (!cleaned) return '';
    
    try {
        // Try to parse the phone number
        const phoneNumber = parsePhoneNumber(cleaned, defaultCountry);
        if (phoneNumber && phoneNumber.isValid()) {
            return phoneNumber.format('E.164');
        }
    } catch (error) {
        // If parsing fails, return cleaned version if it starts with +
        if (cleaned.startsWith('+')) {
            return cleaned;
        }
    }
    
    return '';
};

/**
 * Hashes an email address using SHA-256
 * Used for privacy-first contact matching
 * @param {string} email - Email address to hash
 * @returns {string} - SHA-256 hash of the normalized email
 */
export const hashEmail = (email) => {
    const normalized = normalizeEmail(email);
    if (!normalized) return '';
    return CryptoJS.SHA256(normalized).toString();
};

/**
 * Processes a contact from the Contact Picker API
 * Extracts and normalizes name, emails, and phone numbers
 * @param {Contact} contact - Contact object from Contact Picker API
 * @returns {Object} - Processed contact with normalized data
 */
export const processContact = (contact) => {
    const name = contact.name?.[0] || '';
    
    // Extract and normalize emails
    const emails = (contact.email || [])
        .map(email => normalizeEmail(email))
        .filter(email => email && validateEmail(email));
    
    // Extract and normalize phone numbers
    const phones = (contact.tel || [])
        .map(phone => normalizePhone(phone))
        .filter(phone => phone);
    
    // Get primary email for hashing (first valid email)
    const primaryEmail = emails[0] || '';
    const hashedEmail = primaryEmail ? hashEmail(primaryEmail) : '';
    
    // Get primary phone (first valid phone)
    const primaryPhone = phones[0] || '';
    
    return {
        name: name.trim(),
        emails,
        phones,
        normalizedEmail: primaryEmail,
        normalizedPhone: primaryPhone,
        hashedEmail,
        // Unique identifier for deduplication
        id: hashedEmail || primaryPhone || name.toLowerCase().trim()
    };
};

/**
 * Deduplicates contacts based on normalized email or phone
 * @param {Array} contacts - Array of processed contacts
 * @returns {Array} - Deduplicated contacts array
 */
export const deduplicateContacts = (contacts) => {
    const seen = new Set();
    const unique = [];
    
    for (const contact of contacts) {
        // Use hashed email as primary key, fallback to phone, then name
        const key = contact.hashedEmail || contact.normalizedPhone || contact.id;
        
        if (key && !seen.has(key)) {
            seen.add(key);
            unique.push(contact);
        }
    }
    
    return unique;
};

/**
 * Groups contacts alphabetically by first letter of name
 * @param {Array} contacts - Array of processed contacts
 * @returns {Object} - Object with letter keys and contact arrays as values
 */
export const groupContactsAlphabetically = (contacts) => {
    const grouped = {};
    
    contacts.forEach(contact => {
        const firstLetter = (contact.name.charAt(0) || '#').toUpperCase();
        const letter = /[A-Z]/.test(firstLetter) ? firstLetter : '#';
        
        if (!grouped[letter]) {
            grouped[letter] = [];
        }
        
        grouped[letter].push(contact);
    });
    
    // Sort contacts within each group
    Object.keys(grouped).forEach(letter => {
        grouped[letter].sort((a, b) => 
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
    });
    
    return grouped;
};

/**
 * Detects if device is iOS
 * @returns {boolean} - True if device is iOS
 */
export const isIOSDevice = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    const platform = navigator.platform?.toLowerCase() || '';
    
    // iOS Detection (handles modern iPadOS 13+)
    const isIPad = 
        /iPad/i.test(ua) || 
        (platform === 'macintel' && navigator.maxTouchPoints > 1);
    const isIPhone = /iPhone/i.test(ua);
    const isIPod = /iPod/i.test(ua);
    
    return isIPad || isIPhone || isIPod;
};

/**
 * Detects if device is mobile (iOS or Android)
 * @returns {boolean} - True if device is mobile
 */
export const isMobileDevice = () => {
    if (typeof navigator === 'undefined') return false;
    return isIOSDevice() || /Android/i.test(navigator.userAgent);
};

/**
 * Validates if Contact Picker API is available
 * The Contact Picker API is available on:
 * - Chrome/Edge Android (version 80+)
 * - Chrome Desktop (version 80+, but requires user gesture)
 * Note: Safari iOS does NOT support Contact Picker API
 * @returns {boolean} - True if Contact Picker API is supported
 */
export const isContactPickerSupported = () => {
    return (
        typeof navigator !== 'undefined' &&
        'contacts' in navigator &&
        typeof navigator.contacts === 'object' &&
        'select' in navigator.contacts &&
        typeof navigator.contacts.select === 'function'
    );
};

